import Microgame from '../engine/Microgame.js';

export default class SliceGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Tranche !`, en: `Slice!`, es: `¡Rebana!` },
            controls: { fr: `Glisse ton doigt à travers l'objet rouge volant`, en: `Swipe your finger through the flying red object`, es: `Desliza tu dedo a través del objeto rojo volador` },
            objective: { fr: `Coupe l'objet en deux tel un ninja`, en: `Slice the object in half like a ninja`, es: `Corta el objeto por la mitad como un ninja` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M3 21 21 3M4.6 4.6 19.4 19.4"/></svg>`,
            actions: [
                { type: 'wait', duration: 520 },
                { type: 'swipe', fromX: 42, fromY: 54, toX: 58, toY: 46, duration: 240 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 2200
        };
    }
    constructor(engine) {
        super(engine);
        this.objects = [];
        this.trail = [];
        this.score = 0;
        this.canvas = null;
        this.ctx = null;
        this.gameEnded = false;
        this.sliceAnim = null; // Animation de découpe

        // Progression: 2.5 secondes à x1, 0.75 seconde à x4
        // Calcul amortissement: 1 + (4-1)*factor = 2500/750 = 3.333 => 3*factor = 2.333 => factor = 0.777
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * 0.777;
        this.duration = 2500 * (speedMult / adjustedSpeed);
        this.gravity = 2500;
    }

    setup() {
        this.engine.ui.instruction.textContent = "SLICE!";

        // Create Canvas
        this.canvas = document.createElement('canvas');
        const width = this.container.clientWidth || window.innerWidth;
        const height = this.container.clientHeight || window.innerHeight;
        this.canvas.width = width;
        this.canvas.height = height;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.zIndex = '100';
        this.canvas.style.pointerEvents = 'none'; // Passthrough
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Input
        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerdown', this.onMove);

        // Spawn ONE Object immediately
        this.spawnObject();
    }

    spawnObject() {
        // Safe Dimensions
        const w = this.canvas.width;
        const h = this.canvas.height;
        const r = 40;

        // Position: Random X, Start BELOW screen
        const x = this.isPlayback ? (w * 0.5) : (r + Math.random() * (w - r * 2));
        const y = h + r + 10; // Un peu sous l'écran pour être visible plus vite

        // On adapte la physique au temps restant exact pour s'assurer que c'est slicable :
        const speedMult = this.engine.speedMultiplier || 1.0;
        let maxT = (this.duration / speedMult) / 1000;
        if (maxT > 10 || maxT === Infinity) maxT = 2.5;

        // On veut que l'objet monte à ~65% de la hauteur de l'écran et redescende en maxT
        // Apex à t = maxT * 0.4
        // Résolution de l'équation h_apex = 0.65 * h
        this.gravity = (8.125 * h) / (maxT * maxT);

        // Vitesse Y initiale pour atteindre cet apex
        const vy = -(this.gravity * maxT * 0.4);

        // Vitesse X : se dirige légèrement vers le centre 
        const vx = (w / 2 - x) / (maxT * 0.5);

        this.objects.push({
            x, y, vx, vy, r,
            color: this.primaryColor,
            sliced: false
        });

        console.log(`[SliceGame] Spawned at ${x | 0}, ${y | 0} with VY=${vy | 0}`);
    }

    onMove(e) {
        if (!this.active || this.gameEnded) return;

        // Get relative coordinates
        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Add to trail
        this.trail.push({ x, y, life: 0.5 }); // Short trail

        // Check collisions with objects
        this.objects.forEach(obj => {
            if (!obj.sliced) {
                const dx = x - obj.x;
                const dy = y - obj.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                // Hit detection slightly generous
                if (dist < obj.r * 1.5) {
                    obj.sliced = true;
                    this.gameEnded = true;

                    // Geler le chrono
                    this._frozenTimer = this.engine.timer;
                    this.engine.timer = Infinity;

                    // Calculer l'angle de la tranche à partir du mouvement du doigt
                    let sliceAngle = Math.PI / 4; // défaut diagonal
                    if (this.trail.length >= 2) {
                        const p1 = this.trail[this.trail.length - 2];
                        const p2 = this.trail[this.trail.length - 1];
                        sliceAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                    }

                    // Créer les deux moitiés
                    const perpAngle = sliceAngle + Math.PI / 2;
                    this.sliceAnim = {
                        x: obj.x,
                        y: obj.y,
                        r: obj.r,
                        color: obj.color,
                        sliceAngle,
                        elapsed: 0,
                        duration: 0.5, // 500ms d'animation
                        half1: { dx: Math.cos(perpAngle), dy: Math.sin(perpAngle), rot: 0 },
                        half2: { dx: -Math.cos(perpAngle), dy: -Math.sin(perpAngle), rot: 0 },
                    };
                }
            }
        });
    }

    update(dt) {
        if (!this.ctx || !this.active) return;

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        const dtSec = dt / 1000;
        const gravity = this.gravity || 2500; // Gravité dynamique calculée plus haut

        // Update Objects
        for (let i = 0; i < this.objects.length; i++) {
            const obj = this.objects[i];

            // Physics
            obj.vy += gravity * dtSec;
            obj.x += obj.vx * dtSec;
            obj.y += obj.vy * dtSec;

            // Check if missed (fell off screen completely)
            // Wait until it has gone up and come back down
            if (obj.y > this.canvas.height + 100 && obj.vy > 0 && !obj.sliced && !this.gameEnded) {
                this.gameEnded = true;
                this.lose();
                return;
            }

            // Draw (seulement les objets non slicés)
            if (!obj.sliced) {
                this.ctx.beginPath();
                this.ctx.arc(obj.x, obj.y, obj.r, 0, Math.PI * 2);
                this.ctx.fillStyle = obj.color;
                this.ctx.fill();
                this.ctx.shadowBlur = 20;
                this.ctx.shadowColor = obj.color;

                // Inner Shine
                this.ctx.beginPath();
                this.ctx.arc(obj.x - 10, obj.y - 10, obj.r * 0.3, 0, Math.PI * 2);
                this.ctx.fillStyle = 'rgba(255,255,255,0.5)';
                this.ctx.fill();
            }
        }

        // === Animation de découpe ===
        if (this.sliceAnim) {
            const anim = this.sliceAnim;
            anim.elapsed += dtSec;
            const t = Math.min(anim.elapsed / anim.duration, 1.0);

            // Easing out (décélération)
            const ease = 1 - Math.pow(1 - t, 3);

            const separation = ease * anim.r * 2.5; // Les moitiés s'écartent
            const rotation = ease * 0.6; // Rotation en radians
            const fallY = ease * ease * 120; // Gravité légère vers le bas
            const fadeOut = Math.max(0, 1 - t * 0.6); // Fondu progressif

            // Dessiner les deux moitiés
            for (const half of [anim.half1, anim.half2]) {
                const sign = half === anim.half1 ? 1 : -1;
                const hx = anim.x + half.dx * separation;
                const hy = anim.y + half.dy * separation + fallY;
                const rot = anim.sliceAngle + sign * rotation;

                this.ctx.save();
                this.ctx.globalAlpha = fadeOut;
                this.ctx.translate(hx, hy);
                this.ctx.rotate(rot);

                // Dessiner un demi-cercle
                this.ctx.beginPath();
                this.ctx.arc(0, 0, anim.r, -Math.PI / 2, Math.PI / 2);
                this.ctx.closePath();
                this.ctx.fillStyle = anim.color;
                this.ctx.shadowBlur = 15;
                this.ctx.shadowColor = anim.color;
                this.ctx.fill();

                // Tranche blanche (le bord coupé)
                this.ctx.beginPath();
                this.ctx.moveTo(0, -anim.r);
                this.ctx.lineTo(0, anim.r);
                this.ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                this.ctx.lineWidth = 3;
                this.ctx.stroke();

                this.ctx.restore();
            }

            // Flash de tranche (ligne blanche rapide au moment du slice)
            if (t < 0.3) {
                const flashAlpha = 1 - (t / 0.3);
                this.ctx.save();
                this.ctx.globalAlpha = flashAlpha;
                this.ctx.translate(anim.x, anim.y);
                this.ctx.rotate(anim.sliceAngle);
                this.ctx.beginPath();
                this.ctx.moveTo(-anim.r * 2, 0);
                this.ctx.lineTo(anim.r * 2, 0);
                this.ctx.strokeStyle = '#fff';
                this.ctx.lineWidth = 4;
                this.ctx.shadowBlur = 20;
                this.ctx.shadowColor = '#fff';
                this.ctx.stroke();
                this.ctx.restore();
            }

            // Animation terminée -> Win !
            if (anim.elapsed >= anim.duration) {
                this.sliceAnim = null;
                this.win();
                return;
            }
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerdown', this.onMove);
    }
}
